import axios from 'axios';
import { base_url } from './api-config';
import { Consignment, ConsignmentItem } from '../model/consignment';

const getHeaders = (operation: string, key_id?: number) => {
    let header: any = {
        "Content-Type": "application/json",
        "operation": operation
    };
    if (key_id !== undefined && key_id !== null) {
        header["key-id"] = key_id;
    }
    return {
        headers: header
    };
};

export const SEED_CONSIGNMENTS: Consignment[] = [
    {
        key: 1,
        id: 1,
        document_no: 'CS26080001',
        doc_no: 'CS26080001',
        vendor_id: 1,
        vendor_name: 'ร้านปอ (ของฝากจอมบึง)',
        vendor_code: 'V0001',
        vendor_tel: '081-234-5678',
        consignment_date: '2026-08-26',
        tracking_mode: 'MANUAL',
        total_received_qty: 60,
        total_remaining_qty: 36,
        total_sold_qty: 24,
        total_sales_amount: 405,
        total_owner_payable: 278,
        total_shop_profit: 127,
        item_count: 3,
        status: 'ACTIVE',
        note: 'รอบฝากขายประจำสัปดาห์',
        last_checked_at: '2026-08-26 14:00:00',
        create_date: '2026-08-26 09:00:00',
        items: [
            {
                id: 1,
                consignment_id: 1,
                product_id: 4,
                batch_no: 1,
                item_status: 'ACTIVE',
                product_code: '8852023664811',
                product_name: 'สินค้า A - โก๋แก่รสกุ้ง75ก',
                product_unit_name: 'แพ็ค',
                quantity_received: 20,
                latest_actual_remaining: 7,
                selling_price_per_unit: 15,
                owner_cost_per_unit: 10,
                quantity_sold: 13,
                sales_amount: 195,
                owner_payable: 130,
                shop_profit: 65
            },
            {
                id: 2,
                consignment_id: 1,
                product_id: 8,
                batch_no: 1,
                item_status: 'ACTIVE',
                product_code: '8850157101400',
                product_name: 'สินค้า B - เจเล่ไลท์สตอเบอรี่',
                product_unit_name: 'แพ็ค',
                quantity_received: 10,
                latest_actual_remaining: 4,
                selling_price_per_unit: 25,
                owner_cost_per_unit: 18,
                quantity_sold: 6,
                sales_amount: 150,
                owner_payable: 108,
                shop_profit: 42
            },
            {
                id: 3,
                consignment_id: 1,
                product_id: 12,
                batch_no: 1,
                item_status: 'ACTIVE',
                product_code: '8850123456789',
                product_name: 'สินค้า C - ขนมปังกรอบเนยสด',
                product_unit_name: 'ห่อ',
                quantity_received: 30,
                latest_actual_remaining: 25,
                selling_price_per_unit: 12,
                owner_cost_per_unit: 8,
                quantity_sold: 5,
                sales_amount: 60,
                owner_payable: 40,
                shop_profit: 20
            }
        ]
    }
];

// V2 note: this formula matches Backend's calculate_item_financials() for a
// SINGLE Batch with received/remaining/returned/settled only -- it has no
// concept of quantity_carried_forward, and it has no concept of FIFO
// attribution across multiple Batches of the same product. It remains
// correct only for single-Batch, non-persisted previews (e.g. "what would
// this look like"), and for the fixtures in `consignment.test.tsx`. It
// must NEVER be used to compute or preview
// FIFO-attributed sold/payable across multiple Batches of one product --
// that split is Backend-authoritative only (see consignment-check-modal.tsx).
export const calculateItemConsignment = (
    quantity_received: number,
    latest_actual_remaining: number,
    selling_price_per_unit: number,
    owner_cost_per_unit: number,
    quantity_returned: number = 0,
    quantity_settled: number = 0
) => {
    const qty_rec = Number(quantity_received || 0);
    const qty_rem = Number(latest_actual_remaining !== undefined && latest_actual_remaining !== null ? latest_actual_remaining : qty_rec);
    const qty_returned = Number(quantity_returned || 0);
    const qty_settled = Number(quantity_settled || 0);
    const sell_price = Number(selling_price_per_unit || 0);
    const owner_cost = Number(owner_cost_per_unit || 0);

    const quantity_sold = Math.max(0, qty_rec - qty_rem - qty_returned);
    const unpaid_sold_quantity = Math.max(0, quantity_sold - qty_settled);
    const sales_amount = Number((quantity_sold * sell_price).toFixed(2));
    const owner_payable = Number((quantity_sold * owner_cost).toFixed(2));
    const settled_amount = Number((qty_settled * owner_cost).toFixed(2));
    const unpaid_owner_payable = Number((unpaid_sold_quantity * owner_cost).toFixed(2));
    const shop_profit = Number((sales_amount - owner_payable).toFixed(2));

    return {
        quantity_received: qty_rec,
        latest_actual_remaining: qty_rem,
        quantity_returned: qty_returned,
        selling_price_per_unit: sell_price,
        owner_cost_per_unit: owner_cost,
        quantity_sold,
        quantity_settled: qty_settled,
        unpaid_sold_quantity,
        sales_amount,
        owner_payable,
        settled_amount,
        unpaid_owner_payable,
        shop_profit
    };
};

export const calculateHeaderTotals = (items: ConsignmentItem[]) => {
    let total_received_qty = 0;
    let total_remaining_qty = 0;
    let total_returned_qty = 0;
    let total_sold_qty = 0;
    let total_settled_qty = 0;
    let total_sales_amount = 0;
    let total_owner_payable = 0;
    let total_settled_amount = 0;
    let total_unpaid_owner_payable = 0;
    let total_shop_profit = 0;

    (items || []).forEach(item => {
        const calc = calculateItemConsignment(
            item.quantity_received,
            item.latest_actual_remaining,
            item.selling_price_per_unit,
            item.owner_cost_per_unit,
            item.quantity_returned,
            item.quantity_settled
        );
        total_received_qty += calc.quantity_received;
        total_remaining_qty += calc.latest_actual_remaining;
        total_returned_qty += calc.quantity_returned;
        total_sold_qty += calc.quantity_sold;
        total_settled_qty += calc.quantity_settled;
        total_sales_amount += calc.sales_amount;
        total_owner_payable += calc.owner_payable;
        total_settled_amount += calc.settled_amount;
        total_unpaid_owner_payable += calc.unpaid_owner_payable;
        total_shop_profit += calc.shop_profit;
    });

    return {
        total_received_qty: Number(total_received_qty.toFixed(2)),
        total_remaining_qty: Number(total_remaining_qty.toFixed(2)),
        total_returned_qty: Number(total_returned_qty.toFixed(2)),
        total_sold_qty: Number(total_sold_qty.toFixed(2)),
        total_settled_qty: Number(total_settled_qty.toFixed(2)),
        total_sales_amount: Number(total_sales_amount.toFixed(2)),
        total_owner_payable: Number(total_owner_payable.toFixed(2)),
        total_settled_amount: Number(total_settled_amount.toFixed(2)),
        total_unpaid_owner_payable: Number(total_unpaid_owner_payable.toFixed(2)),
        total_shop_profit: Number(total_shop_profit.toFixed(2)),
        item_count: (items || []).length
    };
};

/*
 * Phase 7C-6C removed the `localStorage` Consignment cache and the
 * `appendLocalMovement` helper that wrote fabricated history rows into
 * it. Nothing reads or writes browser storage for Consignment any more:
 * every row this screen shows came from Backend, or the screen shows an
 * error. `SEED_CONSIGNMENTS` above survives only as fixture data for
 * `consignment.test.tsx`; no runtime path reaches it.
 */

const SERVER_ONLY_CONSIGNMENT_OPERATIONS = new Set([
    'ADD_STOCK',
    'ADD_STOCK_BULK',
    'UPDATE_REMAINING',
    'CHECK',
    // Read-only but Backend-authoritative -- the Website has no FIFO engine
    // of its own (see consignment-check-modal.tsx), so a Preview that
    // can't reach Backend must fail loudly.
    'STOCK_CHECK_PREVIEW',
    'PERIODIC_SETTLEMENT',
    'SETTLE',
    'RETURN_TO_OWNER',
    'CLOSE_ITEM',
    'CLOSE_PERIOD',
    'FINAL_CLOSE',
    // Rather than build a second, divergent Period+FIFO engine on the
    // client just to emulate a read operation, these always require the
    // real Backend -- consistent with the project's V2 principle that
    // Backend is authoritative for Period and Batch state.
    'PERIODS',
    'GET_PERIODS'
]);

/**
 * The ONLY way this Website reads or writes Consignment data.
 *
 * Phase 7C-6C removed the local/demo fallback that used to sit below
 * this function. When Backend could not be reached, it answered from a
 * seeded `localStorage` copy and fabricated history rows whose operator
 * was the string `ระบบ` and whose timestamp came from the browser clock
 * in UTC -- seven hours off the real rows, in the same table, with
 * nothing to tell them apart. During a Pilot whose whole purpose is
 * comparing the system against the shop's paper records, an unreachable
 * server has to look unreachable.
 *
 * A failure now surfaces as a failure. The 401 path is unchanged: the
 * Backend sends a message with it, so it is thrown here and handled by
 * the central auth interceptor exactly as before.
 */
export const api_consignment = async (operation: string, body: any = {}, key_id?: number): Promise<any> => {
    const op = String(operation).toUpperCase().trim();
    try {
        const res = await axios.post(`${base_url}/api/v1/consignment`, JSON.stringify(body), getHeaders(operation, key_id));
        if (res && res.status === 200 && res.data !== undefined && res.data !== null) {
            return res.data;
        }
    } catch (e: any) {
        // A message from Backend is the real reason -- including the 401
        // the auth layer needs to see. Pass it through untouched.
        const errorMsg = e?.response?.data?.message;
        if (errorMsg) {
            if (SERVER_ONLY_CONSIGNMENT_OPERATIONS.has(op) && errorMsg.includes('Unsupported operation')) {
                throw new Error(`Backend environment นี้ยังไม่รองรับ ${op} กรุณาอัปเดต Backend ก่อนทำรายการ`);
            }
            throw new Error(errorMsg);
        }
        // No response at all: offline, DNS, timeout.
        throw new Error('ไม่สามารถเชื่อมต่อ Backend สำหรับข้อมูลฝากขายได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง');
    }

    // A 200 carrying nothing usable is not a reason to invent data either.
    throw new Error(`Backend ส่งผลลัพธ์ ${op} ที่ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง`);
};
